import React, { ReactElement } from "react"
import { Menu, MenuItem } from "@mui/material"
import { Button, ButtonVariant } from "../../atoms/button"
import styled from "styled-components"
import { theme } from "../../../theme"
import { ArrowRight } from "@mui/icons-material"
import { useMenuDropdown } from "./useMenuDropdown"

export type MenuOption = {
  label: string
  onClick?: () => void
  icon?: ReactElement
  subOptions?: MenuOption[]
  leftAligned?: boolean
}

export type ActionButtonWithMenuProps = {
  label?: string
  icon?: ReactElement
  variant: ButtonVariant
  menuOptions: MenuOption[]
  leftAligned?: boolean
}

const MenuWithSubMenu = (props: MenuOption) => {
  const { label, icon, subOptions = [], leftAligned = false } = props
  const { anchorEl, handleClick, handleClose } = useMenuDropdown()

  return (
    <>
      <CustomMenuItem key={label} onClick={handleClick}>
        <MenuItemLabel>
          {icon}
          {label}
        </MenuItemLabel>
        <ArrowRight />
      </CustomMenuItem>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        transformOrigin={{ vertical: "center", horizontal: leftAligned ? "left" : "right" }}
      >
        {subOptions.map((option) => (
          <CustomMenuItem key={option.label} onClick={option.onClick}>
            <MenuItemLabel>
              {option.icon}
              {option.label}
            </MenuItemLabel>
          </CustomMenuItem>
        ))}
      </Menu>
    </>
  )
}

export const ActionButtonWithMenu = (props: ActionButtonWithMenuProps) => {
  const { label, icon, variant, menuOptions, leftAligned } = props
  const { anchorEl, handleClick, handleClose } = useMenuDropdown()

  const renderMenuOption = (option: MenuOption) => {
    if (!!option.subOptions) {
      return (
        <MenuWithSubMenu
          label={option.label}
          icon={option.icon}
          subOptions={option.subOptions}
          leftAligned={leftAligned}
        />
      )
    }
    return (
      <CustomMenuItem key={option.label} onClick={option.onClick}>
        <MenuItemLabel>
          {option.icon}
          {option.label}
        </MenuItemLabel>
      </CustomMenuItem>
    )
  }

  return (
    <>
      <Button variant={variant} icon={icon} onClick={handleClick} dropdown={!!label}>
        {label}
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        {menuOptions.map((option) => renderMenuOption(option))}
      </Menu>
    </>
  )
}

const CustomMenuItem = styled(MenuItem)`
  min-width: 212px;
  background-color: ${theme.color.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.m} ${theme.spacing.l};
`

const MenuItemLabel = styled.div`
  display: flex;
  gap: ${theme.spacing.m};
  & svg {
    width: ${theme.iconSize.l};
    height: ${theme.iconSize.l};
    color: ${theme.color.black4};
  }
`
